import AssignmentOutlinedIcon from "@mui/icons-material/AssignmentOutlined"
import AttachEmailOutlinedIcon from "@mui/icons-material/AttachEmailOutlined"
import BusinessOutlinedIcon from "@mui/icons-material/BusinessOutlined"
import CalendarTodayOutlinedIcon from "@mui/icons-material/CalendarTodayOutlined"
import DeleteOutlinedIcon from "@mui/icons-material/DeleteOutlined"
import DescriptionOutlined from "@mui/icons-material/DescriptionOutlined"
import EditOutlinedIcon from "@mui/icons-material/EditOutlined"
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined"
import FacebookOutlinedIcon from "@mui/icons-material/FacebookOutlined"
import HomeOutlinedIcon from "@mui/icons-material/HomeOutlined"
import InstagramIcon from "@mui/icons-material/Instagram"
import LanguageOutlinedIcon from "@mui/icons-material/LanguageOutlined"
import LinkedInIcon from "@mui/icons-material/LinkedIn"
import PhoneOutlinedIcon from "@mui/icons-material/PhoneOutlined"
import SavingsOutlinedIcon from "@mui/icons-material/SavingsOutlined"
import SMSOutlinedIcon from "@mui/icons-material/SmsOutlined"
import LoadingButton from "@mui/lab/LoadingButton"
import Autocomplete from "@mui/material/Autocomplete"
import Avatar from "@mui/material/Avatar"
import Button from "@mui/material/Button"
import Chip from "@mui/material/Chip"
import Divider from "@mui/material/Divider"
import Fade from "@mui/material/Fade"
import FormControlLabel from "@mui/material/FormControlLabel"
import IconButton from "@mui/material/IconButton"
import InputAdornment from "@mui/material/InputAdornment"
import Paper from "@mui/material/Paper"
import Skeleton from "@mui/material/Skeleton"
import Stack from "@mui/material/Stack"
import TextField from "@mui/material/TextField"
import Tooltip from "@mui/material/Tooltip"
import Typography from "@mui/material/Typography"
import TrapFocus from "@mui/material/Unstable_TrapFocus"
import { styled } from "@mui/material/styles"
import {
  format as formatDate,
  formatDuration,
  intervalToDuration,
  parseISO,
} from "date-fns"
import { isNull } from "lodash"
import { useEffect, useState } from "react"
import { useLocation, useNavigate } from "react-router-dom"

import { putLeadRating } from "../../../api/crm/leadRating"
import { useAuthorizedAxiosClient } from "../../../common/axios"
import CloseableSnackbar, {
  useCloseableSnackbarState,
} from "../../../common/components/CloseableSnackbar"
import Column from "../../../common/components/Column"
import { BaseGlassMixin } from "../../../common/components/GlassMixin"
import Row from "../../../common/components/Row"
import SkeletonStack from "../../../common/components/SkeletonStack"
import { LoadingTextSkeleton } from "../../../common/components/TextSkeleton"
import { formatCurrency, validatePhone } from "../../../common/utils"
import RatingStars, { RatingEditor } from "../candidates/RatingStars"
import { getCandidateTypeLabel } from "../candidates/candidateType"
import { navigateBackToCandidatesList } from "../candidates/navigation"
import AIIncludedIcon from "../components/AI-IncludedIcon"
import CRMStageAutocomplete, {
  useCRMStageAutocompleteState,
} from "../components/CRMStageAutocomplete"
import GreenCircleChip, { GreenCircleIcon } from "../components/GreenCircleChip"
import {
  LikelihoodToMoveChip,
  useTranslateLikelihoodToMove,
} from "../components/LikelihoodToMoveTableCell"
import UpdateCreateCandidateDialog from "../components/UpdateCreateCandidateDialog"
import UpdateCreateCandidateFeedbackDialog, {
  useUpdateCreateCandidateFeedbackDialogState,
} from "../components/UpdateCreateCandidateFeedbackDialog"
import CreateOfferDialog, {
  useCreateOfferDialogState,
} from "../components/offers/CreateOfferDialog"
import { getLeadDetailsActionPolicy } from "../my-mls/leadDetailsActionPolicy"
import {
  CANDIDATE_MAX_STATS,
  addCRMEventToCandidate,
  useGoogleAddressAutocomplete,
  useValidateMaxValueErrorState,
  validateSocialLink,
  validateURL,
} from "../utils"
import CandidateDetailCustomTagsSection from "./CandidateDetailCustomTagsSection"
import ColumnPaper from "./ColumnPaper"
import ConfirmArchiveCandidateDialog from "./ConfirmArchiveCandidateDialog"
import { EVENT_TYPES } from "./data"

export default function CandidateDetailsColumn({
  candidate,
  setCandidate,
  isLoading,
  readOnly = false,
  leadMode = null,
  onEmailButtonClicked,
  onSendSMSButtonClicked,
  onCallButtonClicked,
  onTaskButtonClicked,
}) {
  const isDnc = Boolean(candidate.is_dnc)
  const dncTitle =
    "This lead is on the DNC list and is not eligible for call, SMS, or email"
  const policy = getLeadDetailsActionPolicy({ readOnly, isDnc })
  const { label: likelihoodToMoveLabel } = useTranslateLikelihoodToMove(
    candidate.likelihood_to_move
  )
  const [candidateEditingMirror, setCandidateEditingMirror] = useState({
    crm_stage: null,
  })
  const {
    createOfferDialogOpen,
    setCreateOfferDialogOpen,
    openCreateOfferDialog,
  } = useCreateOfferDialogState()
  const [updateCandidateDialogOpen, setUpdateCandidateDialogOpen] =
    useState(false)
  const [confirmArchiveOpen, setConfirmArchiveOpen] = useState(false)
  const [isArchivingCandidate, setIsArchivingCandidate] = useState(false)
  const navigate = useNavigate()
  const location = useLocation()
  const {
    isOpen: isSnackbarOpen,
    message: snackbarMessage,
    open: openSnackbar,
    close: closeSnackbar,
  } = useCloseableSnackbarState()
  const [isSavingCandidate, setIsSavingCandidate] = useState(false)
  const [phoneError, setPhoneError] = useState(null)
  const [addressInputValue, setAddressInputValue] = useState("")
  const [address, setAddress] = useState(null)
  const { addressOptions } = useGoogleAddressAutocomplete(addressInputValue)
  const [websiteError, setWebsiteError] = useState(null)
  const [facebookLinkError, setFacebookLinkError] = useState(null)
  const [instagramLinkError, setInstagramLinkError] = useState(null)
  const [linkedinLinkError, setLinkedInLinkError] = useState(null)
  const {
    error: salesVolumeError,
    validateMaxValue: validateSalesVolume,
    fieldProps: salesVolumeErrorProps,
  } = useValidateMaxValueErrorState(CANDIDATE_MAX_STATS.salesVolume)
  const {
    error: homesSoldError,
    validateMaxValue: validateHomesSold,
    fieldProps: homesSoldErrorProps,
  } = useValidateMaxValueErrorState(CANDIDATE_MAX_STATS.homesSold)
  const {
    error: yearsInBusinessError,
    validateMaxValue: validateYearsInBusiness,
    fieldProps: yearsInBusinessErrorProps,
  } = useValidateMaxValueErrorState(CANDIDATE_MAX_STATS.yearsInBusiness)
  const {
    crmStageInputValue,
    setCRMStageInputValue,
    crmStages,
    isLoadingCRMStages,
    populateCurrentCRMStage,
  } = useCRMStageAutocompleteState()
  const {
    candidateReceivingFeedback,
    updateCreateCandidateFeedbackDialogOpen,
    openUpdateCreateCandidateFeedbackDialog,
    closeUpdateCreateCandidateFeedbackDialog,
  } = useUpdateCreateCandidateFeedbackDialogState((payload) => {
    const { timeline_event, ...feedback } = payload
    setCandidate((current) => ({
      ...current,
      feedback,
      crm_events: timeline_event
        ? [
            ...(current.crm_events || []),
            {
              ...timeline_event,
              type: timeline_event.type || EVENT_TYPES.feedback,
            },
          ]
        : current.crm_events,
    }))
  })
  const [mlsInfo, setMlsInfo] = useState({
    license_no: null,
    mls_id: null,
    mls_name: null,
    most_transacted_city: null,
    tenure: null,
    brand: null,
    office: null,
    time_in_current_office: null,
  })
  const axios = useAuthorizedAxiosClient()
  const CRM_STAGE_PROPERTY_NAME = "crm_stage"
  const editableProperties = [
    "email",
    "phone",
    "address",
    "website",
    "sales_volume_last_year",
    "homes_sold_last_year",
    "years_in_business",
    "facebook_link",
    "instagram_link",
    "linkedin_link",
    "rating",
    CRM_STAGE_PROPERTY_NAME,
    "company_name",
    "notes",
  ]
  const unsavedPropertiesCount = editableProperties.filter((property) => {
    if (property === CRM_STAGE_PROPERTY_NAME && candidate[property]) {
      return candidate[property].id !== candidateEditingMirror[property]?.id
    }
    return candidate[property] !== candidateEditingMirror[property]
  }).length

  function handleRatingChanged(e, newValue) {
    if (leadMode && policy.rating.enabled) {
      e?.stopPropagation?.()
      setCandidate((current) => ({ ...current, rating: newValue }))
      setCandidateEditingMirror((current) => ({ ...current, rating: newValue }))
      putLeadRating(axios, leadMode.source, leadMode.id, newValue)
        .then(() => {
          openSnackbar("Rating updated successfully")
        })
        .catch(() => {
          openSnackbar("Could not update rating. Try again.")
        })
      return
    }
    handleCandidatePropertyChanged(makeFakeEvent(newValue), "rating")
  }

  function handleCandidatePropertyChanged(e, propertyName) {
    function validateSocialLinkUpdateState(setSocialLinkError) {
      if (validateSocialLink(newValue)) {
        setSocialLinkError(null)
      } else {
        setSocialLinkError("Please type the path part only")
      }
    }

    const newValue = e.target.value
    if (propertyName === "phone") {
      if (validatePhone(newValue)) {
        setPhoneError(null)
      } else {
        setPhoneError("Please enter a valid phone")
      }
    } else if (propertyName === "website") {
      if (validateURL(newValue)) {
        setWebsiteError(null)
      } else {
        setWebsiteError("Please enter a valid URL path")
      }
    } else if (propertyName === "facebook_link") {
      validateSocialLinkUpdateState(setFacebookLinkError)
    } else if (propertyName === "instagram_link") {
      validateSocialLinkUpdateState(setInstagramLinkError)
    } else if (propertyName === "linkedin_link") {
      validateSocialLinkUpdateState(setLinkedInLinkError)
    } else if (propertyName === "sales_volume_last_year") {
      validateSalesVolume(newValue)
    } else if (propertyName === "homes_sold_last_year") {
      validateHomesSold(newValue)
    } else if (propertyName === "years_in_business") {
      validateYearsInBusiness(newValue)
    }
    setCandidateEditingMirror({
      ...candidateEditingMirror,
      [propertyName]: newValue,
    })
  }

  function handleAddressChanged(e, newValue) {
    handleCandidatePropertyChanged(makeFakeEvent(newValue), "address")
    setAddress(newValue)
  }

  function handleSaveCandidateButtonClicked() {
    setIsSavingCandidate(true)
    const formData = new FormData()
    editableProperties.forEach((property) => {
      const candidateMirrorValue = candidateEditingMirror[property]
      const propertyValue =
        property === CRM_STAGE_PROPERTY_NAME
          ? candidateMirrorValue.id
          : candidateMirrorValue
      formData.append(
        property === CRM_STAGE_PROPERTY_NAME ? "crm_stage_write" : property,
        isNull(propertyValue) ? "" : propertyValue
      )
    })
    axios
      .put(`/crm/api/candidates/${candidate.id}/`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      })
      .then(({ data }) => {
        setCandidate(data)
        openSnackbar("Candidate updated")
      })
      .finally(() => setIsSavingCandidate(false))
  }

  function handleCancelButtonClicked() {
    setCandidateEditingMirror({ ...candidate })
    updateAddressStateFromCandidate()
  }

  function handleCreateOfferDialogClosed(offer) {
    setCreateOfferDialogOpen(false)
    if (offer) {
      addCRMEventToCandidate(setCandidate, offer, EVENT_TYPES.offer)
      openSnackbar("Proposal created successfully")
    }
  }

  function handleUpdateCandidateDialogClosed(updatedCandidate) {
    if (updatedCandidate) {
      setCandidate(updatedCandidate)
    }
    setUpdateCandidateDialogOpen(false)
  }

  function handleConfirmArchiveCandidate(confirmed) {
    if (!confirmed) {
      setConfirmArchiveOpen(false)
      return
    }
    setIsArchivingCandidate(true)
    axios
      .delete(`/crm/api/candidates/${candidate.id}/`)
      .then(() => {
        navigateBackToCandidatesList(navigate, location)
      })
      .catch(() => {
        openSnackbar("Could not remove candidate. Try again.")
      })
      .finally(() => {
        setIsArchivingCandidate(false)
        setConfirmArchiveOpen(false)
      })
  }

  function getCommonTextFieldProps(propertyName, label, Icon, type = "text") {
    return {
      variant: "standard",
      label,
      value: candidateEditingMirror[propertyName],
      onChange: readOnly
        ? undefined
        : (e) => handleCandidatePropertyChanged(e, propertyName),
      InputProps: {
        readOnly,
        endAdornment: (
          <InputAdornment position="end">
            <Icon />
          </InputAdornment>
        ),
      },
      type,
      fullWidth: true,
      InputLabelProps: { shrink: true },
    }
  }

  function updateAddressStateFromCandidate() {
    setAddressInputValue(candidate.address || "")
    setAddress(candidate.address || "")
  }

  useEffect(
    () => {
      if (isLoading) {
        return
      }
      setCandidateEditingMirror(candidate)
      populateCurrentCRMStage(candidate)
      updateAddressStateFromCandidate()
      if (candidate.ssot_agent_id) {
        axios
          .get(
            `/realitydb/api/candidate-profile/mls-info/${candidate.ssot_agent_id}/`
          )
          .then(({ data }) => setMlsInfo(data))
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [candidate, isLoading]
  )

  return (
    <ColumnPaper
      sx={{
        pt: 4,
        ...(isDnc
          ? {
              bgcolor: "rgba(211, 47, 47, 0.08)",
            }
          : {}),
        "&:before": {
          display: "none",
        },
      }}
    >
      <Column>
        <Column sx={{ gap: 3 }}>
          <Row sx={{ justifyContent: "space-between", alignItems: "center" }}>
            <Column sx={{ gap: 1 }}>
              <LoadingTextSkeleton width={10} isLoading={isLoading}>
                <Row sx={{ alignItems: "center", gap: 1, flexWrap: "wrap" }}>
                  <Typography
                    variant="h3"
                    sx={{
                      fontWeight: isDnc ? 700 : undefined,
                      color: isDnc ? "error.dark" : "inherit",
                    }}
                  >
                    {candidate.candidate_name}
                  </Typography>
                  {isDnc ? (
                    <Chip
                      size="small"
                      color="error"
                      label="DNC"
                      sx={{ fontWeight: 800 }}
                    />
                  ) : null}
                </Row>
              </LoadingTextSkeleton>
              <RatingStars candidate={candidate} />
            </Column>
            {readOnly ? null : (
              <LoadingTextSkeleton width={5} isLoading={isLoading}>
                <GreenCircleChip
                  label={candidate.crm_stage?.name}
                  icon={<GreenCircleIcon />}
                />
              </LoadingTextSkeleton>
            )}
          </Row>
          <LoadingTextSkeleton width={4} isLoading={isLoading}>
            <Row sx={{ gap: 2 }}>
              <LabeledValueColumn
                LabelComponent={
                  <LabeledColumnHeader>Annual Value</LabeledColumnHeader>
                }
              >
                <Typography variant="h5" sx={{ lineHeight: 1.7 }}>
                  {formatCurrency(candidate.annual_value)}
                </Typography>
              </LabeledValueColumn>
              {readOnly ? null : (
                <>
                  <Divider orientation="vertical" sx={{ height: "48px" }} />
                  <LabeledValueColumn
                    LabelComponent={
                      <Row sx={{ position: "relative" }}>
                        <LabeledColumnHeader>Potential</LabeledColumnHeader>
                        <AIIncludedIcon
                          sx={{
                            fontSize: "14px",
                            position: "absolute",
                            right: -15,
                            top: -4,
                          }}
                        />
                      </Row>
                    }
                  >
                    <LikelihoodToMoveChip
                      score={candidate.likelihood_to_move}
                      label={likelihoodToMoveLabel}
                    />
                  </LabeledValueColumn>
                </>
              )}
            </Row>
          </LoadingTextSkeleton>
          <Row sx={{ gap: 2 }}>
            {isLoading ? (
              Array.from({ length: 6 }).map((_, i) => (
                <TitledButtonColumn key={i}>
                  <Skeleton variant="circular" width={40} height={40} />
                  <Skeleton variant="text" width={40} />
                </TitledButtonColumn>
              ))
            ) : (
              <>
                <TitledIconButton
                  title="Proposal"
                  Icon={DescriptionOutlined}
                  onClick={openCreateOfferDialog}
                  disabled={!policy.proposal.enabled}
                />
                <TitledIconButton
                  title="SMS"
                  tooltip={isDnc ? dncTitle : undefined}
                  Icon={SMSOutlinedIcon}
                  onClick={onSendSMSButtonClicked}
                  disabled={!policy.sms.enabled}
                />
                <TitledIconButton
                  title="Email"
                  tooltip={isDnc ? dncTitle : undefined}
                  Icon={EmailOutlinedIcon}
                  onClick={onEmailButtonClicked}
                  disabled={!policy.email.enabled}
                />
                <TitledIconButton
                  title="Call"
                  tooltip={isDnc ? dncTitle : undefined}
                  Icon={PhoneOutlinedIcon}
                  onClick={onCallButtonClicked}
                  disabled={!policy.call.enabled}
                />
                <TitledIconButton
                  title="Task"
                  Icon={AssignmentOutlinedIcon}
                  onClick={onTaskButtonClicked}
                  disabled={!policy.task.enabled}
                />
                <TitledIconButton
                  title="Edit"
                  Icon={EditOutlinedIcon}
                  onClick={() => setUpdateCandidateDialogOpen(true)}
                  disabled={!policy.edit.enabled}
                />
                {policy.remove.visible ? (
                  <TitledIconButton
                    title="Remove"
                    Icon={DeleteOutlinedIcon}
                    onClick={() => setConfirmArchiveOpen(true)}
                    disabled={isArchivingCandidate}
                  />
                ) : null}
              </>
            )}
          </Row>

          <TitledSection title="My tags">
            <CandidateDetailCustomTagsSection
              candidate={candidate}
              setCandidate={setCandidate}
              isLoading={isLoading}
              openSnackbar={openSnackbar}
              leadMode={leadMode}
            />
          </TitledSection>

          <CreateOfferDialog
            open={createOfferDialogOpen}
            onClose={handleCreateOfferDialogClosed}
            candidate={candidate}
          />
          <UpdateCreateCandidateDialog
            open={updateCandidateDialogOpen}
            onClose={handleUpdateCandidateDialogClosed}
            candidate={candidate}
          />
          <ConfirmArchiveCandidateDialog
            open={confirmArchiveOpen}
            candidateName={candidate.candidate_name}
            onClose={handleConfirmArchiveCandidate}
          />
        </Column>
        <Divider sx={{ mt: 2 }} />
        {isLoading ? (
          <ConnectionStampColumn>
            <Skeleton variant="text" width={220} />
            <Skeleton variant="text" width={300} />
          </ConnectionStampColumn>
        ) : (
          candidate.stamp && (
            <ConnectionStampColumn>
              <Typography
                variant="body1"
                color="text.secondary"
                fontSize="12px"
              >
                Appointment scheduled for
              </Typography>
              <Typography variant="h6" fontWeight={600}>
                {formatDate(
                  parseISO(candidate.stamp),
                  "EEEE, dd LLLL yyyy, hh:mm aa"
                )}
              </Typography>
              {candidate.latest_booking_reschedule ? (
                <Column sx={{ gap: 0.5, mt: 0.5 }}>
                  <Chip
                    size="small"
                    color="warning"
                    variant="outlined"
                    label="Rescheduled"
                    sx={{ alignSelf: "flex-start", fontWeight: 600 }}
                  />
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}
                  >
                    Reason: {candidate.latest_booking_reschedule.reason}
                  </Typography>
                </Column>
              ) : null}
              {candidate.is_concierge_booking ? (
                <Typography variant="body2" color="text.secondary">
                  Booking type: {getCandidateTypeLabel(candidate)}
                </Typography>
              ) : null}
            </ConnectionStampColumn>
          )
        )}
        <Column sx={{ mt: 4, gap: 3 }}>
          <TitledSection title="Quick Access">
            <InfoSection>
              {isLoading ? (
                <SkeletonStack
                  length={2}
                  variant="rectangular"
                  width="100%"
                  height={40}
                />
              ) : (
                <>
                  <CRMStageAutocomplete
                    disabled={readOnly}
                    crmStage={candidateEditingMirror.crm_stage}
                    setCRMStage={(newCRMStage) =>
                      handleCandidatePropertyChanged(
                        makeFakeEvent(newCRMStage),
                        CRM_STAGE_PROPERTY_NAME
                      )
                    }
                    crmStageInputValue={crmStageInputValue}
                    setCRMStageInputValue={setCRMStageInputValue}
                    crmStages={crmStages}
                    isLoadingCRMStages={isLoadingCRMStages}
                    textFieldVariant="standard"
                  />
                  <TextField
                    {...getCommonTextFieldProps(
                      "company_name",
                      "Company Name",
                      BusinessOutlinedIcon,
                      "text"
                    )}
                  />
                </>
              )}
              <Row
                sx={{ justifyContent: "space-between", alignItems: "flex-end" }}
              >
                {isLoading ? (
                  <>
                    <Skeleton variant="rectangular" width="100%" height={36} />
                  </>
                ) : (
                  <>
                    <FormControlLabel
                      label="Rating"
                      labelPlacement="top"
                      control={
                        <RatingEditor
                          rating={candidateEditingMirror.rating}
                          readOnly={readOnly && !policy.rating.enabled}
                          setRating={handleRatingChanged}
                        />
                      }
                      sx={{ alignItems: "flex-start" }}
                    />
                    <Button
                      variant="outlined"
                      color="primary"
                      size="small"
                      disabled={readOnly}
                      onClick={(e) =>
                        openUpdateCreateCandidateFeedbackDialog(e, candidate)
                      }
                    >
                      {candidate.feedback ? "Update feedback" : "Send feedback"}
                    </Button>
                    <UpdateCreateCandidateFeedbackDialog
                      open={updateCreateCandidateFeedbackDialogOpen}
                      onClose={closeUpdateCreateCandidateFeedbackDialog}
                      candidate={candidateReceivingFeedback}
                    />
                  </>
                )}
              </Row>
            </InfoSection>
          </TitledSection>
          <TitledSection title="Personal Details">
            <InfoSection>
              {isLoading ? (
                <SkeletonStack
                  length={4}
                  variant="rectangular"
                  width="100%"
                  height={40}
                />
              ) : (
                <>
                  <TextField
                    {...getCommonTextFieldProps(
                      "email",
                      "Email",
                      AttachEmailOutlinedIcon,
                      "email"
                    )}
                  />
                  <TextField
                    {...getCommonTextFieldProps(
                      "phone",
                      "Phone",
                      PhoneOutlinedIcon,
                      "phone"
                    )}
                    error={!!phoneError}
                    helperText={phoneError}
                  />
                  <Autocomplete
                    disabled={readOnly}
                    noOptionsText={
                      addressInputValue.length === 0
                        ? "Type to search..."
                        : "Address not found"
                    }
                    autoComplete
                    includeInputInList
                    options={addressOptions}
                    inputValue={addressInputValue}
                    onInputChange={(e, newValue) =>
                      setAddressInputValue(newValue)
                    }
                    value={address}
                    onChange={handleAddressChanged}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        variant="standard"
                        InputProps={{
                          ...params.InputProps,
                          endAdornment: (
                            <InputAdornment position="end">
                              <HomeOutlinedIcon />
                            </InputAdornment>
                          ),
                        }}
                        label="Address"
                        placeholder="Type to search..."
                      />
                    )}
                    sx={{
                      "& .MuiInputBase-root": { paddingRight: "0!important" },
                    }}
                  />
                  <TextField
                    {...getCommonTextFieldProps(
                      "website",
                      "Website",
                      LanguageOutlinedIcon,
                      "url"
                    )}
                    error={!!websiteError}
                    helperText={websiteError}
                  />
                </>
              )}
            </InfoSection>
          </TitledSection>
          <TitledSection title="Statistics">
            <InfoSection>
              {isLoading ? (
                <SkeletonStack
                  length={3}
                  variant="rectangular"
                  width="100%"
                  height={40}
                />
              ) : (
                <>
                  <TextField
                    {...getCommonTextFieldProps(
                      "sales_volume_last_year",
                      "Sales Volume",
                      SavingsOutlinedIcon,
                      "number"
                    )}
                    {...salesVolumeErrorProps}
                  />
                  <TextField
                    {...getCommonTextFieldProps(
                      "years_in_business",
                      "Years in Business",
                      CalendarTodayOutlinedIcon,
                      "number"
                    )}
                    {...yearsInBusinessErrorProps}
                  />
                  <TextField
                    {...getCommonTextFieldProps(
                      "homes_sold_last_year",
                      "Homes Sold",
                      HomeOutlinedIcon,
                      "number"
                    )}
                    {...homesSoldErrorProps}
                  />
                </>
              )}
            </InfoSection>
          </TitledSection>
          <TitledSection title="Socials">
            <InfoSection>
              {isLoading ? (
                <SkeletonStack
                  length={3}
                  variant="rectangular"
                  width="100%"
                  height={40}
                />
              ) : (
                <>
                  <TextField
                    {...getCommonTextFieldProps(
                      "facebook_link",
                      "Facebook",
                      FacebookOutlinedIcon,
                      "url"
                    )}
                    error={!!facebookLinkError}
                    helperText={facebookLinkError}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          facebook.com/
                        </InputAdornment>
                      ),
                    }}
                  />
                  <TextField
                    {...getCommonTextFieldProps(
                      "instagram_link",
                      "Instagram",
                      InstagramIcon,
                      "url"
                    )}
                    error={!!instagramLinkError}
                    helperText={instagramLinkError}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          instagram.com/
                        </InputAdornment>
                      ),
                    }}
                  />
                  <TextField
                    {...getCommonTextFieldProps(
                      "linkedin_link",
                      "LinkedIn",
                      LinkedInIcon,
                      "url"
                    )}
                    error={!!linkedinLinkError}
                    helperText={linkedinLinkError}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          linkedin.com/
                        </InputAdornment>
                      ),
                    }}
                  />
                </>
              )}
            </InfoSection>
          </TitledSection>
          {candidate.ssot_agent_id && (
            <TitledSection title="MLS Information">
              <InfoSection>
                <MLSInfoRow title="License Number" value={mlsInfo.license_no} />
                <MLSInfoRow title="MLS ID" value={mlsInfo.mls_id} />
                <MLSInfoRow title="MLS Name" value={mlsInfo.mls_name} />
                <MLSInfoRow
                  title="Most Transacted City"
                  value={mlsInfo.most_transacted_city}
                />
                <DurationMLSInfoRow
                  title="Time in Industry"
                  value={mlsInfo.tenure}
                />
                <MLSInfoRow
                  title="Brand"
                  value={mlsInfo.brand?.name}
                  icon={mlsInfo.brand?.icon}
                />
                <MLSInfoRow title="Office Name" value={mlsInfo.office?.name} />
                <MLSInfoRow title="Office City" value={mlsInfo.office?.city} />
                <DurationMLSInfoRow
                  title="Time at Office"
                  value={mlsInfo.time_in_current_office}
                />
              </InfoSection>
            </TitledSection>
          )}
          <TitledSection title="Profile Notes">
            {isLoading ? (
              <Skeleton variant="rectangular" width="100%" height={96} />
            ) : (
              <TextField
                value={candidateEditingMirror.notes}
                onChange={
                  readOnly
                    ? undefined
                    : (e) => handleCandidatePropertyChanged(e, "notes")
                }
                InputProps={{ readOnly }}
                multiline
                rows={3}
                fullWidth
              />
            )}
          </TitledSection>
        </Column>
      </Column>
      {readOnly ? null : (
        <UnsavedPropertiesDialog
          unsavedPropertiesCount={unsavedPropertiesCount}
          onSaveButtonClicked={handleSaveCandidateButtonClicked}
          onCancelButtonClicked={handleCancelButtonClicked}
          isSavingCandidate={isSavingCandidate}
          saveButtonDisabled={
            websiteError ||
            phoneError ||
            facebookLinkError ||
            instagramLinkError ||
            linkedinLinkError ||
            salesVolumeError ||
            homesSoldError ||
            yearsInBusinessError
          }
        />
      )}
      <CloseableSnackbar
        open={isSnackbarOpen}
        message={snackbarMessage}
        onClose={closeSnackbar}
      />
    </ColumnPaper>
  )
}

function LabeledValueColumn({ LabelComponent, children }) {
  return (
    <Row>
      <Column sx={{ alignItems: "center", gap: 0.5 }}>
        {LabelComponent}
        {children}
      </Column>
    </Row>
  )
}

function TitledIconButton({ title, Icon, onClick, disabled, tooltip }) {
  const button = (
    <TitledButtonColumn
      sx={disabled ? { cursor: "not-allowed", opacity: 0.55 } : undefined}
    >
      <span
        style={
          disabled
            ? { cursor: "not-allowed", display: "inline-flex" }
            : undefined
        }
      >
        <BorderedIconButton onClick={onClick} disabled={disabled}>
          <Icon />
        </BorderedIconButton>
      </span>
      <IconButtonTitle sx={disabled ? { color: "text.disabled" } : undefined}>
        {title}
      </IconButtonTitle>
    </TitledButtonColumn>
  )
  if (!tooltip) {
    return button
  }
  return <Tooltip title={tooltip}>{button}</Tooltip>
}

const LabeledColumnHeader = styled((props) => (
  <Typography variant="body1" {...props} />
))(({ theme }) => ({
  color: theme.palette.text.secondary,
  fontSize: "10px",
  textWrap: "nowrap",
}))

const InfoSection = styled(Column)(({ theme }) => ({
  gap: theme.spacing(2),
}))

const BorderedIconButton = styled((props) => (
  <IconButton color="inherit" size="medium" {...props} />
))(({ theme }) => ({
  ...BaseGlassMixin,
  boxShadow: "0px 1px 2px 0px #1018280D",
  border: `1px solid rgba(255, 255, 255, 1)`,
  "& .MuiSvgIcon-root": {
    width: "16px",
    height: "16px",
    fill: theme.palette.colors.gray700,
  },
  "&.Mui-disabled": {
    backgroundColor: theme.palette.action.disabledBackground,
    color: theme.palette.action.disabled,
    opacity: 1,
    "& .MuiSvgIcon-root": {
      fill: theme.palette.action.disabled,
    },
  },
}))

const TitledButtonColumn = styled(Column)(({ theme }) => ({
  gap: theme.spacing(1),
  alignItems: "center",
  textAlign: "center",
}))

const IconButtonTitle = styled(Typography)(() => ({
  fontSize: "12px",
}))

const ConnectionStampColumn = styled(Column)(({ theme }) => ({
  marginTop: theme.spacing(3),
  alignItems: "center",
  textAlign: "center",
  gap: theme.spacing(1),
}))

function TitledSection({ title, children }) {
  return (
    <Column gap={2}>
      <Typography variant="h5">{title}</Typography>
      {children}
    </Column>
  )
}

function UnsavedPropertiesDialog({
  unsavedPropertiesCount,
  onSaveButtonClicked,
  onCancelButtonClicked,
  isSavingCandidate,
  saveButtonDisabled,
}) {
  return (
    <TrapFocus open disableAutoFocus disableEnforceFocus>
      <Fade in={unsavedPropertiesCount > 0}>
        <UnsavedPropertiesPaper>
          <Row sx={{ gap: 1 }}>
            <LoadingButton
              color="primary"
              variant="contained"
              loading={isSavingCandidate}
              disabled={saveButtonDisabled}
              onClick={onSaveButtonClicked}
            >
              Save
            </LoadingButton>
            <Button variant="outlined" onClick={onCancelButtonClicked}>
              Cancel
            </Button>
          </Row>
          <Typography variant="body1">
            You changed {unsavedPropertiesCount} properties.
          </Typography>
        </UnsavedPropertiesPaper>
      </Fade>
    </TrapFocus>
  )
}

function MLSInfoRow({ title, value, icon }) {
  return (
    <Row sx={{ justifyContent: "space-between", alignItems: "center" }}>
      <Typography
        variant="h6"
        sx={{ color: "text.secondary", fontWeight: 400 }}
      >
        {title}
      </Typography>
      <Row sx={{ justifyContent: "flex-end", alignItems: "center", gap: 0.5 }}>
        {icon && <Avatar src={icon} sx={{ width: "24px", height: "24px" }} />}
        <Typography variant="h6" sx={{ textAlign: "right", fontWeight: 600 }}>
          {value || "--"}
        </Typography>
      </Row>
    </Row>
  )
}

function DurationMLSInfoRow({ title, value }) {
  // See: https://stackoverflow.com/a/65711327/1433559
  return (
    <MLSInfoRow
      title={title}
      value={
        value &&
        formatDuration(intervalToDuration({ start: 0, end: value * 1000 }), {
          format: ["years", "months"],
        })
      }
    />
  )
}

const UnsavedPropertiesPaper = styled(Paper)(({ theme }) => ({
  position: "sticky",
  bottom: theme.spacing(0),
  marginTop: "12px",
  padding: theme.spacing(2),
  display: "flex",
  alignItems: "center",
  gap: theme.spacing(2),
  zIndex: 100,
  ...BaseGlassMixin,
  flexWrap: "wrap",
  justifyContent: "center",
}))

function makeFakeEvent(newValue) {
  return { target: { value: newValue } }
}
